import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'

import ControlArea from '@components/myScores/ControlArea'
import RestoreScoreButton from '@components/myScores/ControlArea/RestoreScoreButton'
import ScoresView from '@components/myScores/ScoresView'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import AlertDialog from '@components/shared/dialogs/AlertDialog'
import { DELETED_PAGE_SIZE } from '@pages/home/loaders/deletedScores.loader'
import SelectionProvider from '@providers/SelectionProvider'
import { useDialogStore } from '@stores/dialog.store'
import ApiRoutes from '@utils/api-routes'
import http from '@utils/http'
import Storage from '@utils/storage'

import type { ScoreViewOptions } from '@components/myScores/utils/types'
import type { DeletedScoresResponse } from '@pages/home/loaders/deletedScores.loader'
import type { ViewType } from '@utils/global'

interface Props {
  initialData: DeletedScoresResponse
  order: string | null
}

const DeletedScoresView: React.FC<Props> = ({ initialData, order }) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)

  const [loading, setLoading] = useState(false)
  const [scoresData, setScoresData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }

  const { scores, remaining, startIndex } = scoresData

  // Used to detect when to infinite scroll
  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<DeletedScoresResponse>(ApiRoutes.members.trashPath(), {
        order,
        max_count: DELETED_PAGE_SIZE,
        start_index: startIndex,
      })

      if (newResp?.data) {
        setScoresData((prevData) => ({
          ...newResp.data,
          scores: [...prevData.scores, ...newResp.data.scores],
        }))
      }
      setLoading(false)
    },
    {
      triggerOnNoScroll: true,
      offset: 32,
    }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setScoresData(initialData)
  }, [initialData])

  if (!scores.length) {
    return (
      <NonIdealState
        title={t('content.empty.trash.title')}
        icon="delete"
        subtitle={t('content.empty.trash.subtitle')}
      />
    )
  }

  const options: ScoreViewOptions = {
    hideSocial: true,
    hideState: true,
    hideCollections: true,
    beforeNavigate: () => {
      openDialog('deleted_score_view_alert')
      return false
    },
  }

  return (
    <SelectionProvider initialItems={scores}>
      <ControlArea viewType={layoutType} setViewType={setViewType} order={order}>
        <RestoreScoreButton />
      </ControlArea>
      <div className="flex-1 overflow-y-auto relative pb-4" ref={scrollRef}>
        <p className="text-gray-text dark:text-gray-text-dm w-full text-center mb-4">
          {t('content.deleteDuration')}
        </p>
        <ScoresView scores={scores} viewType={layoutType} options={options} />
        {remaining && loading && <LoadingSpinner />}
      </div>
      <AlertDialog
        name="deleted_score_view_alert"
        maxWidth={400}
        title={t('content.viewScore')}
        message={t('messages.cantOpenTrashScore')}
      />
    </SelectionProvider>
  )
}

export default DeletedScoresView
